import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Lock, LockOpen } from 'lucide-react';
import { createBloqueo, todayStr, toMin } from '../../lib/citasApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';

/**
 * Crear un cierre o una apertura de agenda. Solo admin.
 *
 *  - Cierre: bloquea el día completo o un tramo (vacaciones, congreso…).
 *  - Apertura: abre un tramo horario extraordinario fuera del horario
 *    semanal (p. ej. un sábado por la mañana puntual).
 *
 * Props:
 *  - open / onOpenChange
 *  - agenda:      agenda afectada
 *  - defaultDate: fecha inicial sugerida (la visible en la página)
 *  - defaultTipo: "cierre" (default) | "apertura"
 *  - onSaved:     callback tras guardar
 */
export default function BloqueoDialog({ open, onOpenChange, agenda, defaultDate, defaultTipo = 'cierre', onSaved }) {
  const [tipo, setTipo] = useState('cierre');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [allDay, setAllDay] = useState(true);
  const [start, setStart] = useState('09:00');
  const [end, setEnd] = useState('14:00');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const isApertura = tipo === 'apertura';

  useEffect(() => {
    if (!open) return;
    const d = defaultDate || todayStr();
    setTipo(defaultTipo === 'apertura' ? 'apertura' : 'cierre');
    setDateFrom(d);
    setDateTo(d);
    setAllDay(defaultTipo !== 'apertura');
    setStart('09:00');
    setEnd('14:00');
    setReason('');
    setSaving(false);
  }, [open, defaultDate, defaultTipo]);

  const onChangeTipo = (v) => {
    setTipo(v);
    // Una apertura siempre es un tramo horario concreto.
    if (v === 'apertura') setAllDay(false);
  };

  const onChangeFrom = (v) => {
    setDateFrom(v);
    // "Hasta" nunca por detrás de "desde".
    if (!dateTo || dateTo < v) setDateTo(v);
  };

  const submit = async () => {
    if (!agenda?.id) return;
    if (!dateFrom || !dateTo) {
      toast.error('Indica las fechas');
      return;
    }
    if (dateTo < dateFrom) {
      toast.error('La fecha "hasta" no puede ser anterior a "desde"');
      return;
    }
    const needsHours = isApertura || !allDay;
    if (needsHours && toMin(end) <= toMin(start)) {
      toast.error('La hora de fin debe ser posterior a la de inicio');
      return;
    }
    setSaving(true);
    try {
      await createBloqueo(agenda.id, {
        tipo,
        date_from: dateFrom,
        date_to: dateTo,
        all_day: isApertura ? false : allDay,
        start: needsHours ? start : undefined,
        end: needsHours ? end : undefined,
        reason: reason.trim() || undefined,
      });
      toast.success(isApertura ? 'Huecos abiertos' : 'Bloqueo creado');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      // detail puede ser string (400/403) o lista de errores (422 de FastAPI).
      const d = e?.response?.data?.detail;
      const msg = Array.isArray(d)
        ? d.map((x) => x?.msg || JSON.stringify(x)).join(' · ')
        : (d || 'No se pudo guardar');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="bloqueo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isApertura
              ? <LockOpen className="w-5 h-5 text-[#2e7d52]" />
              : <Lock className="w-5 h-5 text-[#13231E]" />}
            {isApertura ? 'Abrir huecos extra' : 'Bloquear agenda'}
            {agenda?.name ? ` — ${agenda.name}` : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Tabs value={tipo} onValueChange={onChangeTipo}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="cierre" data-testid="bloqueo-tipo-cierre">Cerrar huecos</TabsTrigger>
              <TabsTrigger value="apertura" data-testid="bloqueo-tipo-apertura">Abrir huecos extra</TabsTrigger>
            </TabsList>
          </Tabs>
          <p className="text-xs text-muted-foreground -mt-2">
            {isApertura
              ? 'Añade un tramo de citas fuera del horario habitual (p. ej. un sábado puntual).'
              : 'Impide dar citas el día completo o en un tramo (vacaciones, congreso, quirófano…).'}
          </p>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Desde</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => onChangeFrom(e.target.value)}
                className="mt-1.5"
                data-testid="bloqueo-date-from"
              />
            </div>
            <div>
              <Label>Hasta</Label>
              <Input
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => setDateTo(e.target.value)}
                className="mt-1.5"
                data-testid="bloqueo-date-to"
              />
            </div>
          </div>

          {!isApertura && (
            <div className="flex items-center gap-3 rounded-md border border-[#D9E3DD] bg-[rgba(46,110,94,0.06)] p-3">
              <Switch checked={allDay} onCheckedChange={(v) => setAllDay(!!v)} data-testid="bloqueo-all-day" />
              <div className="text-sm">
                <div className="font-medium text-[#13231E]">Día completo</div>
                <div className="text-xs text-muted-foreground">Desactívalo para bloquear solo un tramo horario</div>
              </div>
            </div>
          )}

          {(isApertura || !allDay) && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Hora inicio</Label>
                <Input
                  type="time"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                  className="mt-1.5 tabular-nums"
                  data-testid="bloqueo-start"
                />
              </div>
              <div>
                <Label>Hora fin</Label>
                <Input
                  type="time"
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                  className="mt-1.5 tabular-nums"
                  data-testid="bloqueo-end"
                />
              </div>
            </div>
          )}

          <div>
            <Label>Motivo</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={isApertura ? 'Ej.: Sábado extraordinario' : 'Ej.: Vacaciones, congreso, quirófano…'}
              className="mt-1.5"
              data-testid="bloqueo-reason"
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="bloqueo-cancel">
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={saving}
            className={isApertura ? 'bg-[#2e7d52] hover:bg-[#256742]' : 'bg-[#2E6E5E] hover:bg-[#1E4A3F]'}
            data-testid="bloqueo-save"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isApertura ? 'Abrir huecos' : 'Guardar bloqueo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
